/**
 * Centralized query configuration for TanStack Query.
 * 
 * Use these presets to standardize cache behavior across the app.
 * Import and spread in query options: { query: { ...QUERY_CONFIG.FREQUENT } }
 */

/**
 * Query configuration presets based on data freshness requirements
 */
export const QUERY_CONFIG = {
  /**
   * CRITICAL: Data that must always be fresh (inventory, prices, real-time status)
   * - staleTime: 0 (always refetch)
   * - gcTime: 1 minute
   * 
   * Use for: product inventory, prices, payment status, live data
   */
  CRITICAL: {
    staleTime: 0,
    gcTime: 1 * 60 * 1000, // 1 minute
  },

  /**
   * FREQUENT: Data that changes often but can tolerate brief staleness
   * - staleTime: 30 seconds
   * - gcTime: 2 minutes
   * 
   * Use for: lists, user data, notifications, activity feeds
   */
  FREQUENT: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  },

  /**
   * STANDARD: Default for most queries (uses global defaults from QueryClient)
   * - staleTime: 5 minutes
   * - gcTime: 10 minutes
   * 
   * Use for: general data, admin panels, settings, profiles
   */
  STANDARD: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  /**
   * STABLE: Data that rarely changes
   * - staleTime: 30 minutes
   * - gcTime: 1 hour
   * 
   * Use for: configuration, feature flags, roles, permissions
   */
  STABLE: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  /**
   * STATIC: Data that almost never changes
   * - staleTime: 1 hour
   * - gcTime: 24 hours
   * 
   * Use for: countries, languages, timezones, currencies, reference data
   */
  STATIC: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },

  /**
   * IMMUTABLE: Data that never changes (cache forever)
   * - staleTime: Infinity
   * - gcTime: Infinity
   * 
   * Use for: public config, constants loaded once
   */
  IMMUTABLE: {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
} as const;

/**
 * Type for query config preset names
 */
export type QueryConfigPreset = keyof typeof QUERY_CONFIG;











